import {
  EntityTypeEnum,
  SuborderStatusEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamDateFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  useGetSuborders,
  getGetSubordersQueryOptions,
} from "@/requests/gen/react-query/suborder";
import formatUSD from "@/lib/format-currency";
import { format, parseISO } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  parseAsInteger,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Toggle } from "@/components/ui/toggle";
import { Flag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";

const suborderSearchParsers = {
  statuses: parseAsArrayOf(
    parseAsStringEnum(Object.values(SuborderStatusEnum)),
  ),
  fulfilling_pharmacy_ids: parseAsArrayOf(parseAsInteger),
};

const suborderSearchAllParsers = {
  ...dataTableSearchParsers,
  ...suborderSearchParsers,
};

const loadSuborderSearch = createLoader(suborderSearchAllParsers);

type SuborderSearch = inferParserType<typeof suborderSearchAllParsers>;

function suborderQueryParams(
  p: SuborderSearch,
): Parameters<typeof getGetSubordersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    sort_surgery_date: p.sort_column === "surgery_date",
    suborder_statuses: p.statuses,
    fulfilling_pharmacy_ids: p.fulfilling_pharmacy_ids,
  };
}

export const Route = createFileRoute("/_private/_pharmacy-360/suborder/lookup")(
  {
    loader: ({ context: { queryClient }, location }) =>
      queryClient.prefetchQuery(
        getGetSubordersQueryOptions(
          suborderQueryParams(loadSuborderSearch(location.searchStr)),
        ),
      ),
    component: SuborderLookup,
  },
);

// Define the columns for the table
interface SuborderTableProps {
  link?: LinkProps;
  suborder_id: string;
  fulfilling_pharmacy: string;
  created_at: string;
  target_ship_by: string | null;
  total: string;
  status: SuborderStatusEnum | "";
  patient: string;
  surgery_date: string | null;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<SuborderTableProps>[] = [
  { header: "Suborder ID", accessorKey: "suborder_id" },
  { header: "Pharmacy", accessorKey: "fulfilling_pharmacy" },
  {
    header: "Created At",
    accessorKey: "created_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },

    enableSorting: true,
  },
  {
    header: "Target Ship By",
    accessorKey: "target_ship_by",
    cell: ({ row }) => {
      const dateString = row.getValue("target_ship_by") as string;
      const status = row.original.status;
      if (!dateString) return "";

      const targetDate = parseISO(dateString);
      const now = new Date();
      const daysUntilTarget = Math.ceil(
        (targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );

      const variant =
        status !== SuborderStatusEnum.Open
          ? "outline"
          : now > targetDate
            ? "destructive"
            : daysUntilTarget <= 2
              ? "warning"
              : "success";

      return (
        <Badge variant={variant}>{format(targetDate, "MM-dd-yyyy")}</Badge>
      );
    },
    enableSorting: true,
  },
  { header: "Total", accessorKey: "total", enableSorting: true },
  { header: "Status", accessorKey: "status" },
  { header: "Patient", accessorKey: "patient" },
  { header: "Surgery Date", accessorKey: "surgery_date", enableSorting: true },
];

// Main component
function SuborderLookup() {
  const [queryStates, setQueryStates] = useQueryStates(
    suborderSearchAllParsers,
  );

  // Get the suborders
  const suborders = useGetSuborders(suborderQueryParams(queryStates), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: SuborderTableProps[] | undefined = suborders.data?.items.map(
    (suborder) => {
      return {
        link: linkOptions({
          to: "/suborder/$suborderId",
          params: { suborderId: String(suborder.suborder_id) },
        }),
        suborder_id: `${suborder.order_id}-${suborder.suborder_id}`,
        fulfilling_pharmacy: suborder.fulfilling_pharmacy.name,
        created_at: suborder.created_at,
        target_ship_by: suborder.target_ship_by || "",
        total: formatUSD(suborder.total),
        status: suborder.status,
        patient: `${suborder.order.patient.last_name}, ${suborder.order.patient.first_name}`,
        surgery_date: suborder.order.surgery_date,
      };
    },
  );

  // State for search terms
  const [pharmacySearch, setPharmacySearch] = useState<string | undefined>();

  // Get pharmacy entities for filtering
  const pharmacies = useGetEntitiesInfinite(
    {
      entity_types: [EntityTypeEnum.Pharmacy],
      search: pharmacySearch,
      include_ids: queryStates.fulfilling_pharmacy_ids || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  return (
    <div className="text-left">
      <title>Suborders - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Suborders", link: { to: "/suborder/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Suborders</CardTitle>
          <CardDescription>
            View recent suborders below. You can search, filter, and click to
            see more details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={suborders.isFetching} />
            <DropdownFilter
              title="Status"
              options={[
                {
                  items: Object.values(SuborderStatusEnum)
                    .filter((status) => status !== SuborderStatusEnum.Draft)
                    .map((status) => {
                      return { label: status, value: status };
                    }),
                },
              ]}
              values={queryStates.statuses || []}
              onChange={(values) => setQueryStates({ statuses: values })}
            />
            <DropdownFilter
              title="Pharmacy"
              options={[
                {
                  items:
                    pharmacies.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => ({
                        label: entity.name,
                        value: entity.entity_id,
                      })) ?? [],
                },
              ]}
              values={queryStates.fulfilling_pharmacy_ids || []}
              onChange={(values) =>
                setQueryStates({ fulfilling_pharmacy_ids: values })
              }
              search={pharmacySearch}
              setSearch={setPharmacySearch}
              isFetching={pharmacies.isFetchingNextPage}
              fetchNextPage={pharmacies.fetchNextPage}
            />
            <Toggle
              pressed={
                queryStates.sort_column === "target_ship_by" &&
                queryStates.ascending === true &&
                (queryStates.statuses?.includes(SuborderStatusEnum.Open) ??
                  false)
              }
              onPressedChange={(pressed) => {
                setQueryStates({
                  sort_column: pressed ? "target_ship_by" : null,
                  ascending: pressed ? true : null,
                  statuses: pressed ? [SuborderStatusEnum.Open] : null,
                });
              }}
            >
              <Flag />
              Priority
            </Toggle>
            <ParamDateFilter title="Created At" name="created_at" />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={suborders.isLoading}
            isError={suborders.isError}
            count={suborders.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
